import {
  faBuilding,
  faBuildingUser,
  faChartKanban,
  faClock,
  faPaperPlaneTop,
  faShuffle,
  faUser
} from '@fortawesome/pro-solid-svg-icons'
import { Link } from '@tanstack/react-router'
import {
  Fragment,
  type ReactElement,
  useContext,
  useMemo,
  useState
} from 'react'
import { TypeFilter } from '~/components/Filters/TypeFilter'
import { LabelWithIcon } from '~/components/Global/Elements/LabelWithIcon/LabelWithIcon'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { useConfig } from '~/domains/configuration/queries/config'
import { useDashboardContext } from '~/domains/dashboard/context/DashboardContext'
import { useLastActivity } from '~/domains/dashboard/queries/useLastActivity'
import type { DashboardBlockLayoutProps } from '~/domains/dashboard/types'
import { classicLink } from '~/styles/app.css'
import { formatRelativeTime } from '~/utils'
import { ApplicantModalContext } from '~/utils/contextes'
import { DashboardBlockLayout } from '../../DashboardBlock/DashboardBlockLayout'
import {
  activitiesList,
  activityItem,
  activityItemTitle,
  separator
} from './LastActivityBlock.css'

const activityTypeFilterOptions = [
  { label: 'Candidats', value: 'applicant' },
  { label: 'Contacts', value: 'contact' },
  { label: 'Entreprises', value: 'company' },
  { label: 'Offres', value: 'offer' },
  { label: 'Commandes', value: 'order' },
  { label: 'Placements actifs', value: 'push' }
]

export const LastActivityBlock = (
  props: DashboardBlockLayoutProps
): ReactElement => {
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set())
  const { data: legacyUrl = '' } = useConfig('organization.legacy_url')
  const { userIds, isUserIdsLoading } = useDashboardContext()
  const { setApplicantCurrentId } = useContext(ApplicantModalContext)

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useLastActivity({
    types: selectedTypes.size > 0 ? Array.from(selectedTypes) : undefined,
    userIds: userIds.length > 0 ? userIds : undefined
  })

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  const allActivities = useMemo(() => {
    if (!data) return []
    return data.pages.flatMap((page) => page['hydra:member'])
  }, [data])

  const actions = (
    <TypeFilter
      options={activityTypeFilterOptions}
      selectedTypes={selectedTypes}
      onTypesChange={(types) => setSelectedTypes(new Set(types))}
      buttonText="Types"
    />
  )

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'applicant':
        return faUser
      case 'contact':
        return faBuildingUser
      case 'company':
        return faBuilding
      case 'offer':
        return faShuffle
      case 'order':
        return faChartKanban
      case 'push':
        return faPaperPlaneTop
    }
    return null
  }

  const getActivityTitle = (type: string) => {
    switch (type) {
      case 'applicant':
        return 'Candidat créé'
      case 'contact':
        return 'Contact créé'
      case 'company':
        return 'Entreprise créée'
      case 'offer':
        return 'Offre créée'
      case 'order':
        return 'Commande créée'
      case 'push':
        return 'Placement actif envoyé'
    }
    return null
  }

  if (isError) {
    return (
      <DashboardBlockLayout {...props}>
        <Alert
          variation={AlertVariation.Danger}
          message="Erreur lors du chargement des activités"
          onDismiss={false}
        />
      </DashboardBlockLayout>
    )
  }

  if (isLoading || isUserIdsLoading) {
    return (
      <DashboardBlockLayout {...props} actions={actions}>
        <div className={activitiesList}>
          <Shimmer width="100%" height={20} />
          <Shimmer width="100%" height={20} />
          <Shimmer width="100%" height={20} />
        </div>
      </DashboardBlockLayout>
    )
  }

  if (allActivities.length === 0) {
    return (
      <DashboardBlockLayout {...props} actions={actions}>
        <NoResult icon={faClock} message="Aucune activité récente" isSmall />
      </DashboardBlockLayout>
    )
  }

  let actualRelativeTime = ''

  return (
    <DashboardBlockLayout {...props} actions={actions}>
      <div className={activitiesList}>
        {allActivities.map((activity) => {
          let hasSeparator = false
          let relativeTime = ''
          if (activity.date) {
            relativeTime = formatRelativeTime(new Date(activity.date), 2)
            hasSeparator = actualRelativeTime !== relativeTime
            actualRelativeTime = relativeTime
          }
          const icon = getActivityIcon(activity.type)
          const title = activity.title || 'Nom inconnu'
          let titleElement = null
          if (activity.type === 'applicant') {
            titleElement = (
              <span
                className={classicLink}
                onClick={() => {
                  setApplicantCurrentId(activity.id)
                }}
              >
                {title}
              </span>
            )
          }
          if (activity.type === 'order') {
            titleElement = (
              <Link
                className={classicLink}
                to="/orders/$orderId"
                params={{ orderId: String(activity.id) }}
              >
                {title}
              </Link>
            )
          }
          if (activity.type === 'company') {
            titleElement = (
              <a
                className={classicLink}
                href={`${legacyUrl}/admin/entreprises/edit/${activity.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {title}
              </a>
            )
          }
          if (activity.type === 'offer') {
            titleElement = (
              <a
                className={classicLink}
                href={`${legacyUrl}/admin/offres/voir/${activity.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {title}
              </a>
            )
          }
          if (activity.type === 'contact') {
            titleElement = (
              <a
                className={classicLink}
                href={`${legacyUrl}/admin/contacts/modifier/${activity.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {title}
              </a>
            )
          }
          if (activity.type === 'push') {
            titleElement = (
              <a
                className={classicLink}
                href={`${legacyUrl}/admin/push/rechercher?push_id=${activity.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {title}
              </a>
            )
          }
          return (
            <Fragment key={`${activity.id}-${activity.type}`}>
              {hasSeparator && <div className={separator}>{relativeTime}</div>}
              <div className={activityItem}>
                <LabelWithIcon icon={icon ?? undefined}>
                  <div>{getActivityTitle(activity.type)}</div>
                </LabelWithIcon>
                <div className={activityItemTitle}>{titleElement}</div>
              </div>
            </Fragment>
          )
        })}

        {hasNextPage && (
          <Button
            variation={ButtonVariation.Neutral}
            onClick={handleLoadMore}
            fullWidth
            buttonSize="small"
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Chargement...' : "Charger plus d'éléments"}
          </Button>
        )}
      </div>
    </DashboardBlockLayout>
  )
}
